import { useEffect, useState } from 'react';
import { IBundlePlan } from './config';

interface IAddonApiPlan {
  name: string;
  display_name: string;
  price: string;
  quota: number | string;
}

interface IAddonApiResponse {
  availablePlans: IAddonApiPlan[];
  currentPlan: string;
  isInstalled?: boolean;
}

interface IAddonPrices {
  paypalPlans: IBundlePlan[] | null;
  returnsPlans: IBundlePlan[] | null;
  currentPaypalPlan: string | null;
  currentReturnsPlan: string | null;
  paypalInstalled: boolean;
  returnsInstalled: boolean;
}

const PAYPAL_URL = 'https://synctrack-dev.megamind-dev.online/api/shop/internal/info';
const RETURNS_URL = 'https://returns.returnstest.info/s/api/store/internal/info';

const parsePrice = (priceStr: string): number => parseFloat(priceStr.replace('$', '')) || 0;

const capitalize = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const formatQuota = (quota: number | string, unit: string): string => {
  if (String(quota).toLowerCase() === 'unlimited') return `Unlimited ${unit}`;
  const num = parseInt(String(quota), 10);
  return isNaN(num) ? String(quota) : `${num.toLocaleString()} ${unit}/mo`;
};

const mapPlans = (plans: IAddonApiPlan[], unit: string): IBundlePlan[] =>
  plans.map((p) => ({
    id: p.name,
    name: capitalize(p.display_name),
    quota: formatQuota(p.quota, unit),
    price: parsePrice(p.price),
  }));

export const useAddonPrices = (shop: string): IAddonPrices => {
  const [result, setResult] = useState<IAddonPrices>({
    paypalPlans: null,
    returnsPlans: null,
    currentPaypalPlan: null,
    currentReturnsPlan: null,
    paypalInstalled: false,
    returnsInstalled: false,
  });

  useEffect(() => {
    if (!shop) return;

    const fetchOne = (url: string, label: string): Promise<IAddonApiResponse | null> =>
      fetch(`${url}?shop=${encodeURIComponent(shop)}`)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<IAddonApiResponse>;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(`[useAddonPrices] ${label} fetch failed:`, err);
          return null;
        });

    Promise.all([fetchOne(PAYPAL_URL, 'paypal'), fetchOne(RETURNS_URL, 'returns')]).then(
      ([paypal, returns]) => {
        setResult({
          paypalPlans: paypal ? mapPlans(paypal.availablePlans, 'orders') : null,
          returnsPlans: returns ? mapPlans(returns.availablePlans, 'returns') : null,
          currentPaypalPlan: paypal?.currentPlan ?? null,
          currentReturnsPlan: returns?.currentPlan ?? null,
          paypalInstalled: !!paypal?.isInstalled,
          returnsInstalled: !!returns?.isInstalled,
        });
      },
    );
  }, [shop]);

  return result;
};
